import { useState, useEffect, SetStateAction } from "react";
import { BigNumber } from "bignumber.js";
import { Suspense, lazy } from "react";
import { Box, Stack, Fab } from "@mui/material";
import { Helmet } from "react-helmet";
import {
  ArrowForward as ArrowForwardIcon,
  GetApp as GetAppIcon,
} from "@mui/icons-material";
import Header from "../../components/header";
import GoogleMap from "./components/google_map";
import DistanceAndDuration from "./components/distance_and_duration";
import TextWithInput from "./components/text_with_input";
import FuelCost from "./components/fuel_cost";
import { primary } from "../../theme/constants";
import {
  getDistanceBetweenPlaces,
  getPlaceDetails,
} from "../../utils/requests";
import { isNil, round } from "lodash";

const SearchBox = lazy(() => import("./components/search_box"));
let deferredPrompt: any;

const FuelCalculator = () => {
  const [fromDetails, setFromDetails] = useState(null);
  const [toDetails, setToDetails] = useState(null);
  const [distance, setDistance] = useState("");
  const [duration, setDuration] = useState("");
  const [fuelPrice, setFuelPrice] = useState("");
  const [mileage, setMileage] = useState("");
  const [fuelCost, setFuelCost] = useState(0);
  const [distanceInCogs, setDistanceInCogs] = useState(0);
  const [fromCordinates, setFromCordinates] = useState(null);
  const [toCordinates, setToCordinates] = useState(null);
  const [installable, setInstallable] = useState(false);

  const handleFromDetails = async (place: any) => {
    try {
      const placeId = place.place_id;
      const fromGeoCordinates = await getPlaceDetails(placeId);
      setFromDetails(place.description);
      console.log("fromGeoCordinates", fromGeoCordinates);
      setFromCordinates(fromGeoCordinates);
    } catch (error) {
      console.log("handleFromDetails:", error);
    }
  };

  const handleToDetails = async (place: any) => {
    try {
      const placeId = place.place_id;
      const toGeoCordinates = await getPlaceDetails(placeId);
      setToDetails(place.description);
      console.log("toGeoCordinates", toGeoCordinates);
      setToCordinates(toGeoCordinates);
    } catch (error) {
      console.log("handleToDetails:", error);
    }
  };

  const getDistance = async () => {
    try {
      if (!isNil(fromDetails) && !isNil(toDetails)) {
        const response: any = await getDistanceBetweenPlaces(
          fromDetails,
          toDetails
        );
        setDistance(response.distance.text);
        setDuration(response.duration.text);
        setDistanceInCogs(response.distance.value);
      }
    } catch (error) {
      console.log("getDistance:", error);
    }
  };

  const handleFuelPrice = (e: {
    target: { value: SetStateAction<string> };
  }) => {
    setFuelPrice(e.target.value);
  };

  const handleMileage = (e: { target: { value: SetStateAction<string> } }) => {
    setMileage(e.target.value);
  };

  const computeFuelCost = () => {
    const fuelCost = new BigNumber(distanceInCogs)
      .dividedBy(1000)
      .dividedBy(parseFloat(mileage))
      .multipliedBy(parseFloat(fuelPrice))
      .toNumber();
    setFuelCost(round(fuelCost, 2));
  };

  const handleInstallClick = (e: any) => {
    // Hide the app provided install promotion
    setInstallable(false);
    // Show the install prompt
    deferredPrompt.prompt();
    // Wait for the user to respond to the prompt
    deferredPrompt.userChoice.then((choiceResult: any) => {
      if (choiceResult.outcome === "accepted") {
        console.log("User accepted the install prompt");
      } else {
        console.log("User dismissed the install prompt");
      }
    });
  };

  useEffect(() => {
    window.addEventListener("beforeinstallprompt", (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      deferredPrompt = e;
      // Update UI notify the user they can install the PWA
      setInstallable(true);
    });

    window.addEventListener("appinstalled", () => {
      // Log install to analytics
      console.log("INSTALL: Success");
    });
  }, []);

  useEffect(() => {
    if (!isNil(distanceInCogs) && !isNil(fuelPrice) && !isNil(mileage)) {
      computeFuelCost();
    }
  }, [distanceInCogs, fuelPrice, mileage]);

  useEffect(() => {
    if (!isNil(fromDetails) && !isNil(toDetails)) {
      getDistance();
    }
  }, [fromDetails, toDetails]);

  return (
    <>
      <Helmet>
        <script
          id='google-maps'
          src='https://maps.googleapis.com/maps/api/js?key=AIzaSyBQmVJgzxh49P-gpZZ144ovtcsS8Y6qGKc&amp;libraries=places'
        ></script>
      </Helmet>
      <Suspense fallback={<div>Loading...</div>}>
        <Box padding={2} sx={{ "& > :not(style)": { m: 1 } }}>
          <Header />
          <Stack
            marginY={3}
            direction='row'
            spacing={3}
            alignItems='center'
            style={{ alignItems: "center" }}
          >
            <SearchBox onPlaceSelect={handleFromDetails} direction='From' />
            <ArrowForwardIcon
              sx={{ fontSize: 36 }}
              style={{ marginTop: "20px", color: primary }}
            />
            <SearchBox onPlaceSelect={handleToDetails} direction='To' />
          </Stack>
          <GoogleMap destination={toCordinates} origin={fromCordinates} />
          <Box marginY={3}>
            {distance.length > 0 && (
              <DistanceAndDuration distance={distance} duration={duration} />
            )}
          </Box>

          <Stack spacing={3} marginBottom={6}>
            <TextWithInput
              value={fuelPrice}
              onChange={handleFuelPrice}
              label='Fuel price per litre'
              inputIcon=''
            />
            <TextWithInput
              value={mileage}
              onChange={handleMileage}
              label='Mileage of your vehicle'
              inputIcon='km'
            />
          </Stack>
          <Box marginTop={6}>
            <FuelCost fuelCost={fuelCost.toString()} />
          </Box>
          {installable && (
            <Fab
              color='primary'
              aria-label='add'
              sx={{
                position: "fixed",
                bottom: (theme) => theme.spacing(2),
                right: (theme) => theme.spacing(2),
              }}
              onClick={handleInstallClick}
            >
              <GetAppIcon />
            </Fab>
          )}
        </Box>
      </Suspense>
    </>
  );
};

export default FuelCalculator;
